/**
 * Canonical ASCII digits for anything a form or the API will parse as a number.
 *
 * Arabic keyboards emit Arabic-Indic (`٠-٩`) or Persian (`۰-۹`) digits. The
 * backend and SQLite/MySQL integer columns do not treat those as `0-9`.
 * Screens must not each invent a regex for this — one map, one function.
 */
const ARABIC_INDIC = '٠١٢٣٤٥٦٧٨٩';
const PERSIAN_INDIC = '۰۱۲۳۴۵۶۷۸۹';
const ASCII = '0123456789';
const DIGIT_MAP = {};
for (let i = 0; i < 10; i += 1) {
    DIGIT_MAP[ARABIC_INDIC[i] ?? ''] = ASCII[i] ?? '';
    DIGIT_MAP[PERSIAN_INDIC[i] ?? ''] = ASCII[i] ?? '';
}
/** Arabic decimal separator (٫). */
const ARABIC_DECIMAL = '\u066B';
/** Arabic thousands separator (٬). */
const ARABIC_GROUP = '\u066C';
/**
 * Replace Arabic-Indic and Persian digits with ASCII. Every other character
 * is left alone — names, plates, `+`, spaces, letters in a reward code.
 */
export function normalizeDigits(input) {
    let out = '';
    for (const char of input) {
        out += DIGIT_MAP[char] ?? char;
    }
    return out;
}
/**
 * Digit normalisation plus separators that only belong on a numeric field.
 *
 * `digits`: strip Arabic grouping (`٬`) so `١٢٬٥٠٠` is `12500`.
 * `decimal`: also turn Arabic decimal `٫` into `.`.
 * `phone`: digits only; keep `+`, spaces and punctuation the phone parser owns.
 */
export function normalizeNumericInput(input, mode = 'digits') {
    const latin = normalizeDigits(input);
    if (mode === 'phone') {
        return latin.replace(/\u00a0/g, ' ');
    }
    const ungrouped = latin.replaceAll(ARABIC_GROUP, '');
    if (mode === 'decimal') {
        return ungrouped.replaceAll(ARABIC_DECIMAL, '.');
    }
    return ungrouped;
}
/**
 * Fold known numeric keys in a JSON body. Free-text keys (`name`, `notes`)
 * are left alone — Arabic digits in a note stay as the user wrote them.
 */
const INTEGER_KEYS = new Set([
    'year',
    'current_mileage',
    'mileage',
    'next_service_mileage',
    'quantity',
]);
const DECIMAL_KEYS = new Set(['price', 'unit_price', 'total_amount']);
const PHONE_KEYS = new Set(['phone', 'whatsapp']);
const CODE_KEYS = new Set(['code', 'vin', 'plate_number', 'born_on']);
export function canonicalizeNumericPayload(value) {
    if (value === null || value === undefined) {
        return value;
    }
    if (typeof FormData !== 'undefined' && value instanceof FormData) {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map((item) => canonicalizeNumericPayload(item));
    }
    if (typeof value !== 'object') {
        return value;
    }
    const next = {};
    for (const [key, entry] of Object.entries(value)) {
        if (typeof entry === 'string') {
            if (INTEGER_KEYS.has(key)) {
                next[key] = normalizeNumericInput(entry, 'digits');
                continue;
            }
            if (DECIMAL_KEYS.has(key)) {
                next[key] = normalizeNumericInput(entry, 'decimal');
                continue;
            }
            if (PHONE_KEYS.has(key)) {
                next[key] = normalizeNumericInput(entry, 'phone');
                continue;
            }
            if (CODE_KEYS.has(key)) {
                next[key] = normalizeDigits(entry);
                continue;
            }
        }
        next[key] = canonicalizeNumericPayload(entry);
    }
    return next;
}
/** Query strings: only digit characters change, so a name search is unharmed. */
export function canonicalizeQuery(query) {
    if (query === undefined) {
        return undefined;
    }
    const next = {};
    for (const [key, entry] of Object.entries(query)) {
        next[key] = typeof entry === 'string' ? normalizeDigits(entry) : entry;
    }
    return next;
}
/** Keyboard types that mean the field is numeric, not free text. */
export function numericModeForKeyboard(keyboardType) {
    switch (keyboardType) {
        case 'phone-pad':
            return 'phone';
        case 'decimal-pad':
            return 'decimal';
        case 'number-pad':
        case 'numeric':
        case 'numbers-and-punctuation':
            return 'digits';
        default:
            return undefined;
    }
}
//# sourceMappingURL=digits.js.map